import { DateTime } from 'luxon';
import { TimeLogDto, TimeLogStatus } from '../../commonTypes/timelog.types';
import { ProjectAssigneeRateRowDto } from '../../commonTypes/project.types';

export type RateBucketSeconds = { submitted: number; unsubmitted: number };

export type RateBucketKey = string;

const UNMATCHED_PREFIX = '__unmatched__';

export function splitTimelogIntoCalendarDaySeconds(log: TimeLogDto): { day: DateTime; seconds: number }[] {
    if (!log.logEnded) return [];
    const start = DateTime.fromISO(log.logStarted);
    const end = DateTime.fromISO(log.logEnded);
    if (end <= start) return [];

    const segments: { day: DateTime; seconds: number }[] = [];
    let cur = start;
    while (cur < end) {
        const nextDayStart = cur.startOf('day').plus({ days: 1 });
        const segmentEnd = end <= nextDayStart ? end : nextDayStart;
        const sec = Math.round(segmentEnd.diff(cur, 'seconds').seconds);
        if (sec > 0) {
            segments.push({ day: cur.startOf('day'), seconds: sec });
        }
        cur = segmentEnd;
    }
    return segments;
}

function dayContainedInRateRow(dayStart: DateTime, row: ProjectAssigneeRateRowDto): boolean {
    const from = DateTime.fromISO(row.effectiveFrom).startOf('day');
    const to = row.effectiveTo
        ? DateTime.fromISO(row.effectiveTo).endOf('day')
        : DateTime.fromObject({ year: 9999, month: 12, day: 31 }).endOf('day');
    return dayStart >= from && dayStart <= to;
}

export function findRateRowForCalendarDay(
    rateRows: ProjectAssigneeRateRowDto[],
    userSub: string,
    projectId: string,
    day: DateTime
): ProjectAssigneeRateRowDto | null {
    const dayStart = day.startOf('day');
    const candidates = rateRows.filter(r => r.userSub === userSub && r.projectId === projectId && dayContainedInRateRow(dayStart, r));
    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0];
    return [...candidates].sort((a, b) => DateTime.fromISO(b.effectiveFrom).toMillis() - DateTime.fromISO(a.effectiveFrom).toMillis())[0];
}

function ensureBucket(map: Map<RateBucketKey, RateBucketSeconds>, key: RateBucketKey): RateBucketSeconds {
    let b = map.get(key);
    if (!b) {
        b = { submitted: 0, unsubmitted: 0 };
        map.set(key, b);
    }
    return b;
}

export function aggregateTimelogsIntoRateBuckets(
    timelogs: TimeLogDto[],
    rateRows: ProjectAssigneeRateRowDto[],
    projectId: string,
    periodStart: DateTime,
    periodEnd: DateTime
): Map<RateBucketKey, RateBucketSeconds> {
    const map = new Map<RateBucketKey, RateBucketSeconds>();
    const p0 = periodStart.startOf('day');
    const p1 = periodEnd.endOf('day');

    for (const log of timelogs) {
        if (log.projectId !== projectId) continue;

        for (const { day, seconds } of splitTimelogIntoCalendarDaySeconds(log)) {
            if (day < p0 || day > p1) continue;

            const rateRow = findRateRowForCalendarDay(rateRows, log.userSub, projectId, day);
            const key: RateBucketKey = rateRow ? rateRow.id : `${UNMATCHED_PREFIX}${log.userSub}`;
            const bucket = ensureBucket(map, key);
            if (log.status === TimeLogStatus.SUBMITTED) bucket.submitted += seconds;
            else bucket.unsubmitted += seconds;
        }
    }

    return map;
}

export function isUnmatchedBucketKey(key: RateBucketKey): boolean {
    return key.startsWith(UNMATCHED_PREFIX);
}

export function userSubFromUnmatchedKey(key: RateBucketKey): string {
    return key.slice(UNMATCHED_PREFIX.length);
}
